import { Icon } from "@elit-hair/elit-ui";
import { useTranslation } from "react-i18next";
import { TbCheck } from "react-icons/tb";

import { usePriorityReportDepartments } from "../hooks/use-priority-report-departments";
import { PRIORITY_DEPARTMENTS } from "../utils/priority-report.utils";

type Props = {
    /**
     * When provided, the component is controlled — renders the given selection and
     * calls `onToggle` instead of writing to settings directly.
     * When omitted, the component manages its own state via `usePriorityReportDepartments`.
     */
    selectedDepts?: string[];
    onToggle?: (key: string) => void;
    /** Horizontal layout for filter bars; default vertical list for popovers. */
    variant?: "list" | "inline";
};

type DeptSelectorContentProps = {
    selectedDepts: string[];
    toggleDepartment: (key: string) => void;
    variant: "list" | "inline";
};

function PriorityReportDeptSelectorContent({ selectedDepts, toggleDepartment, variant }: DeptSelectorContentProps) {
    const { t } = useTranslation();
    const isInline = variant === "inline";

    return (
        <div className={isInline ? "flex flex-col gap-1.5" : undefined}>
            <p
                className={
                    isInline
                        ? "text-xs font-medium text-muted-foreground"
                        : "text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1 mb-1"
                }
            >
                {t("departments")}
            </p>
            <div className={isInline ? "flex flex-wrap gap-2" : undefined}>
                {PRIORITY_DEPARTMENTS.map((dept) => {
                    const isSelected = selectedDepts.includes(dept.key);
                    return (
                        <button
                            key={dept.key}
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleDepartment(dept.key);
                            }}
                            className={
                                isInline
                                    ? "flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted transition-colors"
                                    : "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors"
                            }
                        >
                            <div
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                                    isSelected ? "bg-primary border-primary" : "border-border"
                                }`}
                            >
                                {isSelected && <Icon icon={TbCheck} size={11} className="text-primary-foreground" />}
                            </div>
                            <span className="font-medium">{dept.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function PriorityReportDeptSelectorUncontrolled({ variant }: Pick<Props, "variant">) {
    const { selectedDepts, toggleDepartment } = usePriorityReportDepartments();

    return (
        <PriorityReportDeptSelectorContent
            selectedDepts={selectedDepts}
            toggleDepartment={toggleDepartment}
            variant={variant ?? "list"}
        />
    );
}

/**
 * Renders department checkboxes for the Priority Report.
 * Used in the full page filter bar, widget popover, and dashboard layout editor.
 */
export const PriorityReportDeptSelector = ({
    selectedDepts: externalDepts,
    onToggle: externalOnToggle,
    variant = "list",
}: Props = {}) => {
    const isControlled = externalDepts !== undefined && externalOnToggle !== undefined;

    if (!isControlled) {
        return <PriorityReportDeptSelectorUncontrolled variant={variant} />;
    }

    return (
        <PriorityReportDeptSelectorContent
            selectedDepts={externalDepts}
            toggleDepartment={externalOnToggle}
            variant={variant}
        />
    );
};
